import { useState } from 'react';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtBR, fmtMoney } from '../lib/format';
import { statusBadge, classifBadge, catPath, buildCategoriaOptions, HOJE } from '../lib/ui';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { PageProps, ContasFilters } from '../App';
import type { ContaAPagar, FormaPagamento } from '../types';

const CHIPS: { id: ContasFilters['status']; label: string }[] = [
  { id: 'a_vencer', label: 'A vencer' },
  { id: 'atrasadas', label: 'Atrasadas' },
  { id: 'pendente', label: 'Pendentes' },
  { id: 'aguardando', label: 'Aguardando' },
  { id: 'pago', label: 'Pagas' },
  { id: 'todos', label: 'Todas' },
];

const GRID = '70px minmax(180px,1.6fr) minmax(140px,1.3fr) 96px 96px 88px 92px 118px';
const num = (s: string) => { const n = parseFloat((s || '').replace(/\./g, '').replace(',', '.')); return isNaN(n) ? 0 : n; };

export function ContasAPagar({ navigate, contasFilters, setContasFilters }: PageProps) {
  const f = contasFilters;
  const set = (patch: Partial<ContasFilters>) => setContasFilters({ ...f, ...patch });

  const list = useFetch(
    // "sem NF" já implica status=pago no backend — não combinar com o filtro de status
    () => api.listContas({ status: f.semNf ? 'todos' : f.status, sem_nf: f.semNf || undefined, search: f.search || undefined, vencimento_de: f.perDe || undefined, vencimento_ate: f.perAte || undefined, limit: 200 }),
    [f.status, f.semNf, f.search, f.perDe, f.perAte],
  );
  const plano = useFetch(() => api.listPlano(), []);
  const catOptions = plano.data ? buildCategoriaOptions(plano.data.items) : [];

  const [classificar, setClassificar] = useState<ContaAPagar | null>(null);
  const [editar, setEditar] = useState<ContaAPagar | null>(null);

  const perAtivo = !!(f.perDe || f.perAte);
  const totalLiq = (list.data?.items ?? []).reduce((s, c) => s + (c.valor_liquido ?? c.valor ?? 0), 0);
  const em7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const fimMes = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10);
  const iniMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>Contas a Pagar</h1>
        <button className="btn dark" onClick={() => navigate('novo')}><Icon name="plus" size={14} />Lançamento</button>
      </div>

      <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
        {CHIPS.map((c) => (
          <span key={c.id} className={`chip ${!f.semNf && f.status === c.id ? 'active' : ''}`} onClick={() => set({ status: c.id, semNf: false })}>{c.label}</span>
        ))}
        <span className={`chip ${f.semNf ? 'active' : ''}`} style={f.semNf ? { background: 'var(--amber-bg, #fef3c7)', color: 'var(--amber, #b45309)' } : undefined}
          title="Contas pagas ainda sem nota fiscal (e não dispensadas)"
          onClick={() => set({ semNf: !f.semNf })}>Sem NF</span>
        <div className="searchbox" style={{ marginLeft: 'auto', width: 260 }}>
          <Icon name="search" size={14} className="muted" />
          <input placeholder="Buscar fornecedor ou descrição…" value={f.search} onChange={(e) => set({ search: e.target.value })} />
        </div>
      </div>

      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', marginBottom: 14 }}>
        <span className="k-label" style={{ fontSize: 11 }}>Período de vencimento</span>
        <div className="row" style={{ gap: 6 }}>
          <span className="text-sm muted">de</span>
          <input type="date" className="input sm mono" style={{ width: 'auto' }} value={f.perDe} onChange={(e) => set({ perDe: e.target.value })} />
          <span className="text-sm muted">até</span>
          <input type="date" className="input sm mono" style={{ width: 'auto' }} value={f.perAte} onChange={(e) => set({ perAte: e.target.value })} />
        </div>
        <span className="chip sm" onClick={() => set({ perDe: HOJE, perAte: em7 })}>Próx. 7 dias</span>
        <span className="chip sm" onClick={() => set({ perDe: iniMes, perAte: fimMes })}>Este mês</span>
        {perAtivo && (
          <span onClick={() => set({ perDe: '', perAte: '' })} style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', cursor: 'pointer' }}>✕ limpar período</span>
        )}
        <span className="text-sm muted" style={{ marginLeft: 'auto' }}>
          {perAtivo ? `total líquido no filtro: ${fmtMoney(totalLiq)}` : 'aplica-se a qualquer aba acima'}
        </span>
      </div>

      <div className="tbl tbl-scroll">
        <div style={{ minWidth: 980 }}>
          <div className="tbl-head" style={{ gridTemplateColumns: GRID }}>
            <span>Venc.</span><span>Fornecedor</span><span>Categoria</span>
            <span className="cell-r">Bruto</span><span className="cell-r">Líquido</span>
            <span>Status</span><span>Classif.</span><span className="cell-r">Ações</span>
          </div>
          {list.loading ? (
            <div className="loading">Carregando…</div>
          ) : (list.data?.items.length ?? 0) === 0 ? (
            <div className="loading">Nenhuma conta neste filtro.</div>
          ) : (
            list.data!.items.map((c) => {
              const st = statusBadge(c);
              const cl = classifBadge(c.classificacao_status);
              return (
                <div key={c.id} className="tbl-row click" style={{ gridTemplateColumns: GRID }} onClick={() => navigate('detalhe', { id: c.id })}>
                  <span className="mono text-sm" style={{ color: 'var(--text-2)' }}>{fmtBR(c.vencimento)}</span>
                  <div className="cell-2">
                    <div className="l1 row" style={{ gap: 6 }}>
                      <span className="ellipsis">{c.fornecedor}</span>
                      {c.status === 'pago' && c.nf_status === 'pendente' && (
                        <span className="chip-sq" style={{ fontSize: 10, padding: '1px 6px', background: 'var(--amber-bg, #fef3c7)', color: 'var(--amber, #b45309)' }} title="Paga, mas sem nota fiscal">sem NF</span>
                      )}
                    </div>
                    <div className="l2">{c.descricao ?? '—'}</div>
                  </div>
                  <span className="text-sm ellipsis" style={{ color: 'var(--text-2)' }}>{catPath(c)}</span>
                  <span className="mono cell-r" style={{ fontSize: 12.5 }}>{fmtMoney(c.valor)}</span>
                  <span className="mono cell-r cell-strong" style={{ fontSize: 12.5 }}>{fmtMoney(c.valor_liquido)}</span>
                  <span><span className={`badge ${st.cls}`}>{st.label}</span></span>
                  <span><span className={`badge ${cl.cls}`}>{cl.label}</span></span>
                  <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                    <button className="btn ghost icon xs" title="Classificar categoria" onClick={() => setClassificar(c)}><Icon name="filter" size={14} /></button>
                    <button className="btn ghost icon xs" title="Editar" onClick={() => setEditar(c)}><Icon name="edit" size={14} /></button>
                    {c.status !== 'pago' && (
                      <button className="btn ghost icon xs green" title="Pagar" onClick={() => navigate('detalhe', { id: c.id, tab: 'pagamento' })}><Icon name="money" size={14} /></button>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className="tbl-note">{list.data ? `${list.data.items.length} de ${list.data.total} lançamentos` : ''}</div>

      {classificar && <ClassificarModal conta={classificar} catOptions={catOptions} onClose={() => setClassificar(null)} onDone={() => { setClassificar(null); list.refetch(); }} />}
      {editar && <EditarModal conta={editar} onClose={() => setEditar(null)} onDone={() => { setEditar(null); list.refetch(); }} />}
    </div>
  );
}

function ClassificarModal({ conta, catOptions, onClose, onDone }:
  { conta: ContaAPagar; catOptions: { id: number; label: string }[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [sel, setSel] = useState<number | ''>(conta.plano_conta_id ?? '');
  const [padrao, setPadrao] = useState(false);
  const [busy, setBusy] = useState(false);

  const salvar = async () => {
    if (!sel) { toast('Escolha uma categoria.', 'err'); return; }
    setBusy(true);
    try { await api.classificarConta(conta.id, Number(sel), padrao); toast('Categoria classificada.'); onDone(); }
    catch { toast('Falha ao classificar.', 'err'); setBusy(false); }
  };

  return (
    <Modal title={`Classificar — ${conta.fornecedor}`} onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={salvar} disabled={busy}>Confirmar</button>
      </>}>
      <div className="m-sub">Atual: {catPath(conta)}</div>
      <label className="flabel">Categoria</label>
      <select className="select" value={sel} onChange={(e) => setSel(e.target.value ? Number(e.target.value) : '')}>
        <option value="">Escolher categoria…</option>
        {catOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <label className="checkline" style={{ marginTop: 12 }}>
        <input type="checkbox" checked={padrao} onChange={(e) => setPadrao(e.target.checked)} />
        definir como padrão do fornecedor
      </label>
    </Modal>
  );
}

function EditarModal({ conta, onClose, onDone }: { conta: ContaAPagar; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [fornecedor, setFornecedor] = useState(conta.fornecedor);
  const [descricao, setDescricao] = useState(conta.descricao ?? '');
  const [valor, setValor] = useState(conta.valor != null ? String(conta.valor).replace('.', ',') : '');
  const [venc, setVenc] = useState(conta.vencimento ? String(conta.vencimento).slice(0, 10) : '');
  const [forma, setForma] = useState<FormaPagamento | ''>(conta.forma_pagamento ?? '');
  const [busy, setBusy] = useState(false);

  const salvar = async () => {
    setBusy(true);
    try {
      await api.editarConta(conta.id, {
        fornecedor: fornecedor.trim(),
        descricao: descricao.trim() || null,
        valor: valor ? num(valor) : null,
        vencimento: venc || null,
        forma_pagamento: forma || null,
      });
      toast('Lançamento atualizado.');
      onDone();
    } catch { toast('Falha ao editar.', 'err'); setBusy(false); }
  };

  return (
    <Modal title="Editar lançamento" onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={salvar} disabled={busy}>Salvar</button>
      </>}>
      <div className="field"><label>Fornecedor</label><input className="input" value={fornecedor} onChange={(e) => setFornecedor(e.target.value)} /></div>
      <div className="field"><label>Descrição</label><input className="input" value={descricao} onChange={(e) => setDescricao(e.target.value)} /></div>
      <div className="grid-3">
        <div><label className="flabel">Valor (R$)</label><input className="input mono" value={valor} onChange={(e) => setValor(e.target.value)} /></div>
        <div><label className="flabel">Vencimento</label><input type="date" className="input" value={venc} onChange={(e) => setVenc(e.target.value)} /></div>
        <div><label className="flabel">Forma</label>
          <select className="select" value={forma} onChange={(e) => setForma(e.target.value as FormaPagamento | '')}>
            <option value="">—</option>
            <option value="boleto">Boleto</option><option value="pix">PIX</option>
            <option value="transferencia">Transferência</option><option value="cartao">Cartão</option><option value="outro">Outro</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}
